import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, Loader2, CheckCircle2, XCircle, X, Trash2, RefreshCw, FileText, Clock, Play } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const STATUS_STYLE = {
  pending:   { label: "Pending",   icon: Clock,   className: "text-amber-700 bg-amber-50 border-amber-300" },
  running:   { label: "Running",   icon: Play,    className: "text-blue-700 bg-blue-50 border-blue-300" },
  completed: { label: "Completed", icon: CheckCircle2, className: "text-green-700 bg-green-50 border-green-300" },
  failed:    { label: "Failed",    icon: XCircle, className: "text-red-700 bg-red-50 border-red-300" },
  cancelled: { label: "Cancelled", icon: X,       className: "text-slate-600 bg-slate-50 border-slate-300" },
};

function fmtBytes(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
}

function fmtDuration(start, end) {
  if (!start) return "—";
  const s = new Date(start).getTime();
  const e = end ? new Date(end).getTime() : Date.now();
  const sec = Math.max(0, Math.round((e - s) / 1000));
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  return `${m}m ${sec % 60}s`;
}

export default function ImportJobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [viewing, setViewing] = useState(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/import/tally/v2/jobs");
      setJobs(data || []);
      // Update viewing job if open
      if (viewing) {
        const updated = (data || []).find((j) => j.id === viewing.id);
        if (updated) setViewing(updated);
      }
    } catch (e) {
      // ignore — auto-poll
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // NOTE: Auto-polling intentionally removed. Tally imports take seconds-to-minutes; constant
  // 2s polling against Cloud Run produced unnecessary background traffic (~1.8K req/hour while
  // the page is open). Users refresh manually via the "Refresh" button, the upload-success
  // callback, or by re-opening the page. The stale-job reaper on the backend handles dead jobs
  // automatically — no client polling needed for correctness.

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let ok = 0, failed = 0, conflict = 0;
    // Files are imported one by one — backend runs each synchronously and the response
    // body is the final job document (status `completed` or `failed`). No polling needed.
    for (const f of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post(
          "/import/tally/v2/jobs", fd,
          {
            headers: { "Content-Type": "multipart/form-data" },
            // The big Sales CSV can take ~60 s on Cloud Run. Give axios a 5-min ceiling.
            timeout: 5 * 60 * 1000,
          }
        );
        if (data.status === "completed") {
          ok += 1;
          toast.success(`${f.name}: ${data.summary || "imported"}`);
        } else if (data.status === "failed") {
          failed += 1;
          toast.error(`${f.name}: ${data.error || "failed"}`);
        } else {
          ok += 1;
          toast.info(`${f.name}: ${data.status}`);
        }
      } catch (e) {
        if (e.response?.status === 409) {
          conflict += 1;
          toast.warning(`${f.name}: ${e.response.data?.detail || "Duplicate"}`);
        } else if (e.code === "ECONNABORTED") {
          failed += 1;
          toast.error(`${f.name}: Took longer than 5 min — please split this CSV by date range and retry, or contact admin for an offline import.`);
        } else {
          failed += 1;
          toast.error(`${f.name}: ${formatApiError(e)}`);
        }
      }
    }
    setUploading(false);
    if (ok > 0) toast.success(`${ok} file(s) imported successfully`);
    if (failed > 0) toast.error(`${failed} file(s) failed`);
    if (conflict > 0) toast.info(`${conflict} duplicate(s) refused`);
    if (fileRef.current) fileRef.current.value = "";
    load();
  };

  const cancel = async (id) => {
    if (!confirm("Cancel this job?")) return;
    try { await api.post(`/import/tally/v2/jobs/${id}/cancel`); toast.success("Cancel requested"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const forceFail = async (id) => {
    if (!confirm("Force-fail this stuck job?\n\nUse only when a job has been Running for >10 min with no progress (worker process likely died). This frees the slot so you can re-upload the same file. No already-imported data is removed.")) return;
    try { await api.post(`/import/tally/v2/jobs/${id}/force-fail`); toast.success("Job force-failed — you can re-upload now"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this job record?")) return;
    try { await api.delete(`/import/tally/v2/jobs/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const onDrop = (e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); };

  return (
    <div className="space-y-4" data-testid="import-jobs-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Tally Import Jobs</h1>
          <p className="text-sm text-slate-500">Upload Tally CSV exports — each runs in the foreground; the upload returns when import completes.</p>
        </div>
        <Button variant="outline" onClick={() => navigate("/bulk-import")} className="rounded-none" data-testid="back-to-import">
          ← Back to Bulk Import
        </Button>
      </div>

      {/* Upload dropzone */}
      <label
        htmlFor="job-files"
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="block border-2 border-dashed border-slate-300 hover:border-slate-500 bg-slate-50 hover:bg-slate-100 transition-colors p-8 text-center cursor-pointer rounded-sm"
        data-testid="job-dropzone"
      >
        {uploading ? <Loader2 className="w-8 h-8 mx-auto mb-2 text-slate-500 animate-spin" /> : <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />}
        <div className="text-sm font-medium text-slate-700">{uploading ? "Queuing jobs..." : "Drop Tally CSV files here, or click to select"}</div>
        <div className="text-xs text-slate-500 mt-1">Multi-file supported. Each file becomes a separate background job. Identical pending/running uploads are refused.</div>
        <input ref={fileRef} id="job-files" type="file" multiple accept=".csv" className="hidden"
          onChange={(e) => handleFiles(e.target.files)} data-testid="job-file-input" />
      </label>

      {/* Job list */}
      <Card className="rounded-sm border-slate-200 overflow-hidden">
        <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="text-sm font-medium">All jobs ({jobs.length})</div>
          <Button variant="ghost" size="sm" onClick={load} className="rounded-none h-7 text-xs" data-testid="job-refresh">
            <RefreshCw className="w-3 h-3 mr-1" />Refresh
          </Button>
        </div>
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">File</th>
              <th className="px-3 py-2 text-left">Kind</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Counts</th>
              <th className="px-3 py-2 text-left">Duration</th>
              <th className="px-3 py-2 text-left">Created by</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">No jobs yet — drop a Tally CSV above.</td></tr>
            ) : jobs.map((j) => {
              const ss = STATUS_STYLE[j.status] || STATUS_STYLE.pending;
              const Icon = ss.icon;
              const totalCount = Object.values(j.counts || {}).reduce((a, b) => a + b, 0);
              return (
                <tr key={j.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`job-row-${j.id}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <span className="font-mono text-xs">{j.filename}</span>
                      <span className="text-[10px] text-slate-400">{fmtBytes(j.size_bytes || 0)}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 capitalize text-slate-700">{j.kind || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 border ${ss.className}`}>
                      <Icon className="w-3 h-3" />{ss.label}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-700">{totalCount > 0 ? <b>{totalCount.toLocaleString()}</b> : "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{fmtDuration(j.started_at, j.finished_at)}</td>
                  <td className="px-3 py-2 text-slate-600">{j.created_by_name}</td>
                  <td className="px-3 py-2 text-right space-x-1">
                    <Button variant="ghost" size="sm" onClick={() => setViewing(j)} className="h-7 text-xs" data-testid={`job-view-${j.id}`}>View</Button>
                    {(j.status === "pending" || j.status === "running") && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => cancel(j.id)} className="h-7 text-xs text-red-600" data-testid={`job-cancel-${j.id}`}>Cancel</Button>
                        <Button variant="ghost" size="sm" onClick={() => forceFail(j.id)} className="h-7 text-xs text-orange-600" data-testid={`job-force-fail-${j.id}`} title="Use when job is stuck running with no progress for >10 min">Force fail</Button>
                      </>
                    )}
                    {j.status !== "running" && j.status !== "pending" && (
                      <Button variant="ghost" size="sm" onClick={() => del(j.id)} className="h-7" data-testid={`job-delete-${j.id}`}><Trash2 className="w-3.5 h-3.5 text-slate-500" /></Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Job detail dialog */}
      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <FileText className="w-4 h-4" />{viewing?.filename}
              <Button variant="ghost" size="sm" className="ml-auto rounded-none h-7 text-xs" data-testid="job-detail-refresh"
                onClick={async () => {
                  try {
                    const { data } = await api.get(`/import/tally/v2/jobs/${viewing.id}`);
                    setViewing(data);
                    // Also patch into the table list without a full reload
                    setJobs((prev) => prev.map((j) => (j.id === data.id ? data : j)));
                  } catch (e) { toast.error(formatApiError(e)); }
                }}
              >
                <RefreshCw className="w-3 h-3 mr-1" />Refresh
              </Button>
            </DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Field label="Kind" value={viewing.kind || "—"} />
                <Field label="Status" value={STATUS_STYLE[viewing.status]?.label || viewing.status} />
                <Field label="Duration" value={fmtDuration(viewing.started_at, viewing.finished_at)} />
                <Field label="Size" value={fmtBytes(viewing.size_bytes || 0)} />
              </div>

              {viewing.counts && Object.keys(viewing.counts).length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Counts</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1 text-xs">
                    {Object.entries(viewing.counts).map(([k, v]) => (
                      <div key={k} className="bg-slate-50 border border-slate-200 px-2 py-1 flex items-center justify-between">
                        <span className="text-slate-600">{k.replace(/_/g, " ")}</span>
                        <span className="font-semibold tabular-nums">{Number(v).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {viewing.error && (
                <div className="bg-red-50 border border-red-200 p-2 text-xs text-red-800">
                  <div className="font-semibold mb-1">Error</div>{viewing.error}
                </div>
              )}

              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Last 30 log lines</div>
                <pre className="bg-slate-950 text-slate-100 text-xs p-3 rounded-sm max-h-96 overflow-y-auto font-mono leading-relaxed" data-testid="job-log-pre">
{(viewing.log_tail || []).join("\n") || "(no log yet)"}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 px-2 py-1">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-semibold text-sm">{value}</div>
    </div>
  );
}
